// src/modules/nlp/TFIDFLab.jsx
// TF-IDF Lab — Term Importance · Vectorization · Cosine Similarity · Document Retrieval · VSM · Clustering
// Tabs: input | tfidf | vectors | similarity | retrieval | clustering | bm25 | concepts

import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  ScatterChart, Scatter, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  LineChart, Line, Legend, AreaChart, Area,
} from 'recharts';
import { tokenizeWords, removeStopWords, computeTFIDF, cosineSimilarity, tfidfVector, jaccardSim, SAMPLE_TEXTS } from '../../utils/nlpUtils.js';
import { Panel, InfoBox } from '../../components/ui.jsx';

const ACCENT = 'var(--violet)';
const P = ['var(--cyan)','var(--green)','var(--violet)','var(--gold)','var(--red)','var(--orange)','#f472b6','#34d399'];
const TABS = ['input','tfidf','vectors','similarity','retrieval','clustering','bm25','concepts'];

const Btn = ({onClick,children,color=ACCENT,disabled,style})=>(
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Tag = ({children,color=ACCENT})=>(
  <span style={{padding:'2px 8px',background:color+'20',border:`1px solid ${color}44`,color,fontSize:'9px',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:700}}>{children}</span>
);
const MC = ({label,value,sub,color=ACCENT})=>(
  <div style={{background:'var(--raised)',border:`1px solid ${color}22`,borderRadius:'3px',padding:'12px 14px'}}>
    <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'4px'}}>{label}</div>
    <div style={{fontSize:'18px',fontWeight:700,fontFamily:'Syne',color}}>{value}</div>
    {sub&&<div style={{fontSize:'10px',color:'var(--t3)',marginTop:'2px'}}>{sub}</div>}
  </div>
);

const DEFAULT_DOCS = [
  { id:'tech',    label:'AI & Technology', text: SAMPLE_TEXTS.tech },
  { id:'review',  label:'Product Review',  text: SAMPLE_TEXTS.review },
  { id:'science', label:'Quantum Science', text: SAMPLE_TEXTS.science },
  { id:'story',   label:'Short Story',     text: SAMPLE_TEXTS.story },
  { id:'mixed',   label:'Mixed News',      text: SAMPLE_TEXTS.mixed },
];

export default function TFIDFLab() {
  const [tab, setTab]           = useState('input');
  const [docs, setDocs]         = useState(DEFAULT_DOCS);
  const [editIdx, setEditIdx]   = useState(null);
  const [editText, setEditText] = useState('');
  const [result, setResult]     = useState(null);
  const [activeDoc, setActiveDoc] = useState(0);
  const [query, setQuery]       = useState('machine learning artificial intelligence');
  const [simScores, setSimScores] = useState([]);
  const [simMatrix, setSimMatrix] = useState([]);
  const [clusterK, setClusterK] = useState(2);
  const [clusters, setClusters] = useState(null);
  const [bm25k, setBm25k]       = useState(1.5);
  const [bm25b, setBm25b]       = useState(0.75);
  const [bm25Query, setBm25Query] = useState('quantum computing machine learning');
  const [bm25Scores, setBm25Scores] = useState([]);

  const runTFIDF = () => {
    const r = computeTFIDF(docs);
    setResult(r);
    // Compute full similarity matrix
    const vecs = docs.map((_,di) => tfidfVector(removeStopWords(tokenizeWords(docs[di].text)), r.idf, r.allTerms));
    const mat  = vecs.map((a,i) => vecs.map((b,j) => i===j?1:cosineSimilarity(a,b)));
    setSimMatrix(mat);
  };

  const runRetrieval = () => {
    if (!result) { runTFIDF(); return; }
    const qTokens = removeStopWords(tokenizeWords(query));
    const qVec    = tfidfVector(qTokens, result.idf, result.allTerms);
    const scores  = docs.map((d,di) => {
      const dVec = tfidfVector(removeStopWords(tokenizeWords(d.text)), result.idf, result.allTerms);
      return { label:d.label, sim:cosineSimilarity(qVec,dVec), jaccard:jaccardSim(query,d.text) };
    }).sort((a,b)=>b.sim-a.sim);
    setSimScores(scores);
  };

  // BM25 scoring
  const runBM25 = () => {
    const qTokens = removeStopWords(tokenizeWords(bm25Query));
    const avgDL   = docs.reduce((s,d)=>s+tokenizeWords(d.text).length,0)/docs.length;
    const df      = {};
    docs.forEach(d => {
      const toks = new Set(removeStopWords(tokenizeWords(d.text)));
      toks.forEach(t => { df[t]=(df[t]||0)+1; });
    });
    const scores = docs.map(d => {
      const tokens = removeStopWords(tokenizeWords(d.text));
      const tf     = {};
      tokens.forEach(t=>{tf[t]=(tf[t]||0)+1;});
      const dl = tokens.length;
      let score = 0;
      qTokens.forEach(term => {
        const f   = tf[term] || 0;
        const idf = Math.log((docs.length - (df[term]||0) + 0.5) / ((df[term]||0) + 0.5) + 1);
        const bm  = idf * (f*(bm25k+1)) / (f + bm25k*(1 - bm25b + bm25b*dl/avgDL));
        score += bm;
      });
      return { label:d.label, score:+score.toFixed(4) };
    }).sort((a,b)=>b.score-a.score);
    setBm25Scores(scores);
  };

  // Simple k-means on TF-IDF vectors
  const runClustering = () => {
    if (!result) return;
    const vecs = docs.map((_,di) => tfidfVector(removeStopWords(tokenizeWords(docs[di].text)), result.idf, result.allTerms));
    const k    = Math.min(clusterK, docs.length);
    // Init centroids as first k vecs
    let centroids = vecs.slice(0,k).map(v=>[...v]);
    let labels    = new Array(docs.length).fill(0);

    for (let iter=0; iter<20; iter++) {
      // Assign
      labels = vecs.map(v => {
        let best=0, bestSim=-Infinity;
        centroids.forEach((c,ci)=>{ const s=cosineSimilarity(v,c); if(s>bestSim){bestSim=s;best=ci;} });
        return best;
      });
      // Update centroids
      centroids = Array.from({length:k},(_,ci)=>{
        const members = vecs.filter((_,di)=>labels[di]===ci);
        if (!members.length) return centroids[ci];
        return members[0].map((_,ti)=>members.reduce((s,v)=>s+v[ti],0)/members.length);
      });
    }
    const grouped = Array.from({length:k},(_,ci)=>({
      cluster: ci,
      docs: docs.filter((_,di)=>labels[di]===ci).map(d=>d.label),
    }));
    setClusters(grouped);
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      {/* Header */}
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?ACCENT:'transparent'}`,background:tab===t?ACCENT+'18':'transparent',color:tab===t?ACCENT:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>
        ))}
      </div>

      {/* ── INPUT / CORPUS ── */}
      {tab==='input' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="DOCUMENT CORPUS" accent={ACCENT}>
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.7}}>
              TF-IDF operates over a <strong style={{color:'var(--violet)'}}>corpus</strong> (collection of documents). Term importance is relative — a word common in one doc but rare across the corpus scores high. Edit documents below or add your own.
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
              {docs.map((doc,i)=>(
                <div key={doc.id} style={{padding:'10px 14px',background:'var(--raised)',border:`1px solid ${editIdx===i?ACCENT:'var(--b0)'}`,borderRadius:'3px'}}>
                  {editIdx===i ? (
                    <div>
                      <textarea value={editText} onChange={e=>setEditText(e.target.value)} style={{width:'100%',height:'80px',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none',resize:'vertical',boxSizing:'border-box',marginBottom:'8px'}}/>
                      <div style={{display:'flex',gap:'6px'}}>
                        <Btn onClick={()=>{setDocs(d=>d.map((dd,j)=>j===i?{...dd,text:editText}:dd));setEditIdx(null);setResult(null);}} color="var(--green)" style={{padding:'5px 12px'}}>SAVE</Btn>
                        <Btn onClick={()=>setEditIdx(null)} color="var(--t3)" style={{padding:'5px 12px'}}>CANCEL</Btn>
                      </div>
                    </div>
                  ) : (
                    <div style={{display:'flex',alignItems:'flex-start',gap:'10px'}}>
                      <span style={{fontSize:'11px',color:P[i%P.length],fontWeight:700,fontFamily:'JetBrains Mono',minWidth:'20px'}}>D{i+1}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:'11px',fontWeight:700,color:'var(--t1)',marginBottom:'3px'}}>{doc.label}</div>
                        <div style={{fontSize:'10px',color:'var(--t3)'}}>{doc.text.slice(0,100)}…</div>
                        <div style={{fontSize:'9px',color:'var(--t3)',marginTop:'3px'}}>{tokenizeWords(doc.text).length} words · {removeStopWords(tokenizeWords(doc.text)).length} content words</div>
                      </div>
                      <button onClick={()=>{setEditIdx(i);setEditText(doc.text);}} style={{padding:'4px 10px',border:'1px solid var(--b1)',background:'var(--raised)',color:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>EDIT</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{marginTop:'12px'}}>
              <Btn onClick={()=>{setResult(null);runTFIDF();setTab('tfidf');}} color={ACCENT}>COMPUTE TF-IDF →</Btn>
            </div>
          </Panel>
        </div>
      )}

      {/* ── TF-IDF ANALYSIS ── */}
      {tab==='tfidf' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {!result && (
            <div style={{padding:'20px',textAlign:'center'}}>
              <Btn onClick={()=>{runTFIDF();}} color={ACCENT}>COMPUTE TF-IDF →</Btn>
            </div>
          )}

          {result && (
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
                <MC label="Total terms" value={result.allTerms.length} color={ACCENT}/>
                <MC label="Documents"   value={result.N} color="var(--green)"/>
                <MC label="Avg doc len" value={Math.round(docs.reduce((s,d)=>s+removeStopWords(tokenizeWords(d.text)).length,0)/docs.length)+'w'} color="var(--gold)"/>
                <MC label="Vocab density" value={+(result.allTerms.length/docs.reduce((s,d)=>s+removeStopWords(tokenizeWords(d.text)).length,0)).toFixed(3)} sub="terms/word" color="var(--orange)"/>
              </div>

              {/* Formula explanation */}
              <Panel title="THE TF-IDF FORMULA" accent={ACCENT}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px',marginBottom:'14px'}}>
                  {[
                    ['TF(t,d)','Term Frequency','var(--cyan)','count(t in d) / |d|','How often term t appears in document d, normalized by doc length. Prevents long docs from dominating.'],
                    ['IDF(t)','Inverse Doc Freq','var(--gold)','log((N+1)/(df(t)+1)) + 1','Downweights terms that appear in many documents. log makes it sublinear. +1 is Laplace smoothing.'],
                    ['TF-IDF','Combined Score','var(--violet)','TF(t,d) × IDF(t)','High score = frequent in this doc AND rare across corpus. Identifies discriminative terms.'],
                  ].map(([name,full,color,formula,desc])=>(
                    <div key={name} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}44`,borderRadius:'4px'}}>
                      <div style={{fontSize:'14px',fontWeight:700,fontFamily:'JetBrains Mono',color,marginBottom:'4px'}}>{name}</div>
                      <div style={{fontSize:'10px',color:'var(--t2)',marginBottom:'6px',fontWeight:600}}>{full}</div>
                      <div style={{padding:'6px 8px',background:'var(--deep)',borderRadius:'2px',fontFamily:'JetBrains Mono',fontSize:'11px',color:'var(--code)',marginBottom:'8px'}}>{formula}</div>
                      <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                    </div>
                  ))}
                </div>
              </Panel>

              {/* Global top terms */}
              <Panel title="GLOBAL TOP TERMS BY AVG TF-IDF" accent={ACCENT}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={result.globalTop.slice(0,20)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="term" stroke="var(--t3)" tick={{fontSize:8}} angle={-30} textAnchor="end" height={55} interval={0}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                    <Tooltip/>
                    <Bar dataKey="avgTfidf" radius={[2,2,0,0]} name="Avg TF-IDF">
                      {result.globalTop.slice(0,20).map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>

              {/* Per-document */}
              <Panel title="TOP TF-IDF TERMS PER DOCUMENT" accent="var(--cyan)">
                <div style={{display:'flex',gap:'6px',marginBottom:'12px',flexWrap:'wrap'}}>
                  {docs.map((d,i)=>(
                    <button key={i} onClick={()=>setActiveDoc(i)} style={{padding:'5px 14px',border:`1px solid ${activeDoc===i?P[i%P.length]:'var(--b0)'}`,background:activeDoc===i?P[i%P.length]+'18':'var(--raised)',color:activeDoc===i?P[i%P.length]:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:activeDoc===i?700:400}}>{d.label}</button>
                  ))}
                </div>
                {result.topPerDoc[activeDoc] && (
                  <>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'6px',marginBottom:'12px'}}>
                      {result.topPerDoc[activeDoc].map((t,i)=>(
                        <div key={t.term} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 10px',background:'var(--raised)',borderRadius:'2px',border:'1px solid var(--b0)'}}>
                          <span style={{fontSize:'11px',color:'var(--t1)',fontFamily:'JetBrains Mono',flex:1}}>
                            <span style={{color:'var(--t3)',marginRight:'6px',fontSize:'9px'}}>#{i+1}</span>{t.term}
                          </span>
                          <div style={{display:'flex',gap:'8px'}}>
                            <span style={{fontSize:'10px',color:ACCENT,fontWeight:700}}>{t.tfidf}</span>
                            <span style={{fontSize:'9px',color:'var(--t3)'}}>tf:{t.tf}</span>
                            <span style={{fontSize:'9px',color:'var(--gold)'}}>idf:{t.idf}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={result.topPerDoc[activeDoc].slice(0,10)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="term" stroke="var(--t3)" tick={{fontSize:8}} angle={-20} textAnchor="end" height={50} interval={0}/>
                        <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                        <Tooltip/>
                        <Bar dataKey="tfidf" fill={P[activeDoc%P.length]} opacity={.8} radius={[2,2,0,0]} name="TF-IDF"/>
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                )}
              </Panel>

              {/* IDF scores */}
              <Panel title="IDF SCORES — TERM RARITY ACROSS CORPUS" accent="var(--gold)">
                <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'10px'}}>
                  Low IDF = common across all docs (stopwords, if not removed). High IDF = rare, appears in few documents = more discriminative.
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={result.globalTop.slice(0,20).sort((a,b)=>b.idf-a.idf)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="term" stroke="var(--t3)" tick={{fontSize:8}} angle={-25} textAnchor="end" height={50} interval={0}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                    <Tooltip/>
                    <Bar dataKey="idf" fill="var(--gold)" opacity={.8} radius={[2,2,0,0]} name="IDF score"/>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── VECTORS ── */}
      {tab==='vectors' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {!result && <div style={{padding:'20px',textAlign:'center'}}><Btn onClick={()=>{runTFIDF();}} color={ACCENT}>COMPUTE TF-IDF FIRST →</Btn></div>}
          {result && (
            <>
              <Panel title="VECTOR SPACE MODEL — DOCUMENT VECTORS" accent={ACCENT}>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'12px'}}>
                  Each document becomes a vector in |V|-dimensional term space. Documents with similar topics have similar vectors — they point in the same direction. This is the Vector Space Model (Salton 1975), the foundation of information retrieval.
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                  {/* Radar chart of top terms per doc */}
                  <div>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>TF-IDF PROFILE — TOP 8 GLOBAL TERMS</div>
                    {(()=>{
                      const topTerms = result.globalTop.slice(0,8).map(t=>t.term);
                      const radarData = topTerms.map(term => {
                        const pt = {term};
                        docs.forEach((d,di)=>{
                          const s = result.topPerDoc[di].find(t=>t.term===term)?.tfidf||0;
                          pt[d.label.split(' ')[0]] = s;
                        });
                        return pt;
                      });
                      return (
                        <ResponsiveContainer width="100%" height={260}>
                          <RadarChart data={radarData}>
                            <PolarGrid stroke="var(--b1)"/>
                            <PolarAngleAxis dataKey="term" tick={{fontSize:9,fill:'var(--t3)'}}/>
                            <PolarRadiusAxis tick={{fontSize:8,fill:'var(--t3)'}} angle={90}/>
                            {docs.map((d,i)=>(
                              <Radar key={d.id} name={d.label.split(' ')[0]} dataKey={d.label.split(' ')[0]} stroke={P[i%P.length]} fill={P[i%P.length]} fillOpacity={0.08} strokeWidth={1.5}/>
                            ))}
                            <Legend wrapperStyle={{fontSize:'9px'}}/>
                            <Tooltip/>
                          </RadarChart>
                        </ResponsiveContainer>
                      );
                    })()}
                  </div>

                  {/* Vector sparsity */}
                  <div>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>VECTOR SPARSITY PER DOCUMENT</div>
                    {(()=>{
                      const data = docs.map((d,i)=>{
                        const nonzero = result.topPerDoc[i].length;
                        const total   = result.allTerms.length;
                        return { label:d.label.split(' ')[0], nonzero, zero:total-nonzero, sparsity:+((total-nonzero)/total*100).toFixed(1) };
                      });
                      return (
                        <>
                          <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={data}>
                              <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                              <XAxis dataKey="label" stroke="var(--t3)" tick={{fontSize:9}}/>
                              <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                              <Tooltip/>
                              <Legend wrapperStyle={{fontSize:'9px'}}/>
                              <Bar dataKey="nonzero" fill="var(--cyan)" opacity={.8} radius={[2,2,0,0]} name="Non-zero dims" stackId="a"/>
                              <Bar dataKey="zero"    fill="var(--b1)"  opacity={.5} radius={[0,0,0,0]} name="Zero dims" stackId="a"/>
                            </BarChart>
                          </ResponsiveContainer>
                          <InfoBox color={ACCENT} icon="◉" style={{marginTop:'8px'}}>
                            TF-IDF vectors are extremely sparse — most entries are 0. A 50K-term vocabulary produces 50K-dim vectors where {'>'}98% are zeros. Sparse storage (only store non-zeros) is essential. Dense neural embeddings (300d) solve the curse of dimensionality.
                          </InfoBox>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </Panel>

              {/* TF-IDF variants */}
              <Panel title="TF-IDF VARIANTS & WEIGHTING SCHEMES" accent={ACCENT}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  {[
                    ['Raw TF','var(--cyan)','tf(t,d) = count(t,d)','Raw count. Biased toward long documents. Simple but effective for many tasks.'],
                    ['Log-normalized TF','var(--green)','tf(t,d) = 1 + log(count(t,d))','Dampens high-frequency terms. Used by default in sklearn TfidfTransformer.'],
                    ['Augmented TF','var(--gold)','tf(t,d) = 0.5 + 0.5×count/max_count','Prevents bias toward longer docs. Divides by max freq in doc. Used in some IR systems.'],
                    ['Smooth IDF','var(--violet)','idf(t) = log((1+N)/(1+df(t)))+1','Adds 1 to numerator and denominator. Prevents IDF=0 for universal terms. sklearn default.'],
                    ['Max IDF','var(--orange)','idf(t) = log(N/df(t))','Pure IDF without smoothing. Can produce very large values for rare terms.'],
                    ['Sublinear TF + IDF','var(--red)','tf=log(count+1), idf=smooth','Most common production variant. sklearn: sublinear_tf=True. Better correlation with relevance.'],
                    ['BM25 TF','#f472b6','tf = f×(k+1)/(f+k×(1-b+b×dl/avgdl))','Okapi BM25 saturation. TF contribution saturates as freq grows. k=1.2-2.0, b=0.75.'],
                    ['Pivoted Length Norm','#34d399','norm = 1-b+b×dl/avgdl','Alternative to BM25 normalization. Pivoted around average document length. Good for uneven doc lengths.'],
                  ].map(([name,color,formula,desc])=>(
                    <div key={name} style={{padding:'10px 12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'3px'}}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:'5px'}}>
                        <span style={{fontSize:'11px',fontWeight:700,color}}>{name}</span>
                        <code style={{fontSize:'9px',color:'var(--code)',fontFamily:'JetBrains Mono'}}>{formula}</code>
                      </div>
                      <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.55}}>{desc}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── SIMILARITY ── */}
      {tab==='similarity' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {!result && <div style={{padding:'20px',textAlign:'center'}}><Btn onClick={()=>{runTFIDF();}} color={ACCENT}>COMPUTE TF-IDF FIRST →</Btn></div>}
          {result && simMatrix.length > 0 && (
            <>
              <Panel title="DOCUMENT-DOCUMENT COSINE SIMILARITY MATRIX" accent={ACCENT}>
                <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px'}}>
                  cos(A,B) = (A·B) / (|A|×|B|). Range [0,1] for non-negative TF-IDF vectors. 1 = identical direction, 0 = orthogonal (no shared terms).
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{borderCollapse:'collapse',fontSize:'10px',fontFamily:'JetBrains Mono',minWidth:'400px'}}>
                    <thead><tr>
                      <th style={{padding:'8px 12px',color:'var(--t3)',fontSize:'9px'}}>—</th>
                      {docs.map((d,i)=><th key={i} style={{padding:'8px 12px',color:P[i%P.length],fontSize:'9px',textAlign:'center',maxWidth:'80px',wordBreak:'break-word'}}>{d.label.split(' ')[0]}</th>)}
                    </tr></thead>
                    <tbody>
                      {simMatrix.map((row,i)=>(
                        <tr key={i}>
                          <td style={{padding:'8px 12px',color:P[i%P.length],fontWeight:700,fontSize:'9px'}}>{docs[i].label.split(' ')[0]}</td>
                          {row.map((val,j)=>{
                            const bg = i===j?'var(--raised)':`rgba(176,138,255,${val*0.6})`;
                            return (
                              <td key={j} style={{padding:'8px 12px',textAlign:'center',background:bg,color:i===j?'var(--t3)':val>0.3?'var(--t1)':'var(--t3)',fontWeight:val>0.3&&i!==j?700:400,borderRadius:'2px'}}>
                                {i===j?'1.00':val.toFixed(3)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>

              <Panel title="SIMILARITY HEATMAP INTERPRETATION" accent={ACCENT}>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
                  {[
                    ['> 0.5','var(--green)','High similarity — documents share many content words and topics. Likely same domain.'],
                    ['0.2 – 0.5','var(--gold)','Moderate similarity — some shared vocabulary, related but different topics.'],
                    ['< 0.2','var(--red)','Low similarity — different domains, minimal vocabulary overlap. Orthogonal in vector space.'],
                  ].map(([range,color,desc])=>(
                    <div key={range} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'3px'}}>
                      <div style={{fontSize:'13px',fontWeight:700,fontFamily:'JetBrains Mono',color,marginBottom:'6px'}}>{range}</div>
                      <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                    </div>
                  ))}
                </div>
              </Panel>

              <Panel title="COSINE vs OTHER SIMILARITY MEASURES" accent="var(--cyan)">
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  {[
                    ['Cosine Similarity','var(--cyan)','cos(A,B) = (A·B)/(|A||B|)','Length-independent: normalizes by vector magnitude. Best for TF-IDF. Two docs with same term distribution but different lengths → similarity=1. Standard in IR.'],
                    ['Jaccard Similarity','var(--gold)','|A∩B|/|A∪B|','Set-based: ratio of shared vocabulary. Ignores frequency, only presence/absence. Good for binary features. Less effective than cosine for TF-IDF.'],
                    ['Euclidean Distance','var(--orange)','√Σ(aᵢ-bᵢ)²','Sensitive to vector magnitude (long docs score lower). Works poorly for sparse high-dim TF-IDF. Useful after normalization.'],
                    ['Dot Product','var(--green)','Σ aᵢbᵢ','Unnormalized cosine. Biased toward longer docs. Only use when all vectors are L2-normalized (as in DPR, FAISS).'],
                    ['BM25','#f472b6','Probabilistic','Non-vector similarity. Saturates TF contribution. Length normalization. Standard in Elasticsearch, Lucene. Consistently outperforms cosine+TF-IDF on ad-hoc retrieval.'],
                    ['Soft Cosine','var(--violet)','Uses term similarity matrix','Accounts for semantic similarity between terms. "car" and "automobile" contribute to each other. Uses pretrained word embedding similarity matrix.'],
                  ].map(([name,color,formula,desc])=>(
                    <div key={name} style={{padding:'10px 12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'3px'}}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:'5px'}}>
                        <span style={{fontSize:'11px',fontWeight:700,color}}>{name}</span>
                        <code style={{fontSize:'9px',color:'var(--code)',fontFamily:'JetBrains Mono'}}>{formula}</code>
                      </div>
                      <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.55}}>{desc}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── RETRIEVAL ── */}
      {tab==='retrieval' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="DOCUMENT RETRIEVAL — QUERY SEARCH" accent="var(--cyan)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.7}}>
              Convert the query to a TF-IDF vector using corpus IDF weights, then rank documents by cosine similarity. This is how classic search engines worked before neural retrieval.
            </div>
            <div style={{display:'flex',gap:'8px',marginBottom:'8px'}}>
              <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Type a search query…" style={{flex:1,background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'9px 12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none'}}/>
              <Btn onClick={()=>{if(!result)runTFIDF();runRetrieval();}} color="var(--cyan)">SEARCH →</Btn>
            </div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'12px'}}>
              {['machine learning artificial intelligence','quantum computing qubits','ship ocean lighthouse story','product quality customer service','amazon investment cloud stock'].map(q=>(
                <button key={q} onClick={()=>setQuery(q)} style={{padding:'4px 10px',border:'1px solid var(--b0)',background:'var(--raised)',color:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}
                  onMouseEnter={e=>e.currentTarget.style.color='var(--cyan)'} onMouseLeave={e=>e.currentTarget.style.color='var(--t3)'}>{q}</button>
              ))}
            </div>

            {simScores.length > 0 && (
              <>
                <div style={{marginBottom:'12px'}}>
                  {simScores.map((s,i)=>(
                    <div key={s.label} style={{display:'flex',alignItems:'center',gap:'12px',padding:'10px 14px',background:'var(--raised)',borderRadius:'3px',marginBottom:'6px',border:`1px solid ${i===0?'var(--cyan)':'var(--b0)'}`,transition:'all .15s'}}>
                      <div style={{width:'24px',height:'24px',borderRadius:'50%',background:P[i%P.length]+'30',border:`2px solid ${P[i%P.length]}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                        <span style={{fontSize:'10px',fontWeight:700,color:P[i%P.length]}}>{i+1}</span>
                      </div>
                      <span style={{fontSize:'12px',color:'var(--t1)',flex:1,fontWeight:i===0?600:400}}>{s.label}</span>
                      <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
                        <div style={{width:'160px',height:'6px',background:'var(--deep)',borderRadius:'3px'}}>
                          <div style={{height:'100%',width:`${s.sim*100}%`,background:i===0?'var(--cyan)':P[i%P.length],borderRadius:'3px',transition:'width .4s'}}/>
                        </div>
                        <span style={{fontSize:'11px',color:i===0?'var(--cyan)':P[i%P.length],fontFamily:'JetBrains Mono',fontWeight:700,width:'46px',textAlign:'right'}}>cos:{s.sim}</span>
                        <span style={{fontSize:'10px',color:'var(--t3)',fontFamily:'JetBrains Mono',width:'52px',textAlign:'right'}}>jac:{s.jaccard}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={simScores}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="label" stroke="var(--t3)" tick={{fontSize:9}} angle={-15} textAnchor="end" height={44}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}} domain={[0,1]}/>
                    <Tooltip/>
                    <Legend wrapperStyle={{fontSize:'9px'}}/>
                    <Bar dataKey="sim"     fill="var(--cyan)"  opacity={.85} radius={[2,2,0,0]} name="Cosine sim"/>
                    <Bar dataKey="jaccard" fill="var(--gold)" opacity={.7}  radius={[2,2,0,0]} name="Jaccard sim"/>
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}
          </Panel>

          <Panel title="HOW QUERY VECTORIZATION WORKS" accent="var(--cyan)">
            <div style={{display:'flex',gap:'6px',alignItems:'center',flexWrap:'wrap',marginBottom:'12px'}}>
              {['query','→','tokenize','→','remove stops','→','lookup IDF','→','build vector','→','cosine rank'].map((step,i)=>(
                step==='→'
                  ? <span key={i} style={{color:'var(--t3)',fontSize:'14px'}}>→</span>
                  : <div key={i} style={{padding:'7px 12px',background:'var(--violet)18',border:'1px solid var(--violet)44',borderRadius:'3px',fontSize:'10px',color:'var(--violet)',fontFamily:'JetBrains Mono',fontWeight:600}}>{step}</div>
              ))}
            </div>
            <InfoBox color="var(--cyan)" icon="◉">
              Key insight: the query uses the same IDF weights learned from the corpus. A query term that doesn't appear in any document gets IDF=0 → zero contribution. Out-of-vocabulary terms are silently ignored. This is why BM25 / neural retrieval (DPR, ColBERT) often outperform TF-IDF for keyword-light queries.
            </InfoBox>
          </Panel>
        </div>
      )}

      {/* ── CLUSTERING ── */}
      {tab==='clustering' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="DOCUMENT CLUSTERING ON TF-IDF VECTORS" accent="var(--green)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.7}}>
              Run k-means clustering on TF-IDF document vectors. Documents with similar term distributions cluster together. Useful for exploring corpus structure without labels.
            </div>
            <div style={{display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap',marginBottom:'12px'}}>
              <span style={{fontSize:'10px',color:'var(--t3)'}}>K clusters:</span>
              {[2,3,4,5].map(k=>(
                <button key={k} onClick={()=>setClusterK(k)} style={{padding:'5px 14px',border:`1px solid ${clusterK===k?'var(--green)':'var(--b0)'}`,background:clusterK===k?'var(--ggreen)':'var(--raised)',color:clusterK===k?'var(--green)':'var(--t2)',fontSize:'11px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:clusterK===k?700:400}}>{k}</button>
              ))}
              <Btn onClick={()=>{if(!result)runTFIDF();else runClustering();}} color="var(--green)">CLUSTER →</Btn>
            </div>

            {clusters && (
              <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'10px'}}>
                {clusters.map(({cluster,docs:cdocs})=>(
                  <div key={cluster} style={{padding:'14px',background:'var(--raised)',border:`1px solid ${P[cluster%P.length]}44`,borderRadius:'4px'}}>
                    <div style={{fontSize:'11px',fontWeight:700,color:P[cluster%P.length],marginBottom:'8px'}}>CLUSTER {cluster+1}</div>
                    {cdocs.map(label=>(
                      <div key={label} style={{padding:'6px 10px',background:P[cluster%P.length]+'15',borderRadius:'2px',marginBottom:'4px',fontSize:'11px',color:'var(--t1)',fontFamily:'JetBrains Mono'}}>{label}</div>
                    ))}
                    {cdocs.length===0&&<div style={{fontSize:'10px',color:'var(--t3)'}}>Empty cluster</div>}
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="CLUSTERING ALGORITHMS FOR TEXT" accent="var(--green)">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                ['K-Means','var(--green)','Assigns each doc to nearest centroid (by cosine). Iterates until convergence. Requires K upfront. Struggles with high-dim sparse vectors. Works OK on TF-IDF with cosine. O(n×k×iter×|V|).'],
                ['Spherical K-Means','var(--cyan)','L2-normalize all vectors first → cosine = dot product → standard k-means works. Much better than raw k-means on TF-IDF. Used in Gensim, Whoosh. Effective for text.'],
                ['Hierarchical (Ward)','var(--violet)','Agglomerative: merge closest pair at each step. Produces dendrogram — can cut at any K. Ward linkage minimizes within-cluster variance. Scalable to ~10K docs.'],
                ['LDA (Topic Model)','var(--gold)','Latent Dirichlet Allocation: probabilistic soft clustering. Each doc = mixture of topics, each topic = distribution over words. Interpretable. Requires tuning α, β, K. Standard in Gensim.'],
                ['DBSCAN','var(--orange)','Density-based: finds arbitrary-shaped clusters. Marks outliers as noise. No K needed. Works poorly on high-dim TF-IDF (all points equidistant). Better on 2D UMAP/t-SNE projections.'],
                ['BERTopic','var(--red)','Modern: BERT embeddings → UMAP → HDBSCAN → c-TF-IDF for topic labeling. Best quality for semantic clustering. Slow but state-of-art. Handles dynamic topics. huggingface/bertopic.'],
              ].map(([title,color,desc])=>(
                <div key={title} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                  <div style={{color,fontWeight:700,fontSize:'11px',marginBottom:'7px'}}>{title}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.65}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── BM25 ── */}
      {tab==='bm25' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="BM25 — OKAPI BEST MATCH 25" accent="var(--orange)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'14px',lineHeight:1.7}}>
              BM25 is the dominant IR scoring function since 1994. Default in Elasticsearch, Lucene, Solr, Whoosh. Addresses two problems with TF-IDF: (1) TF saturation — very frequent terms get diminishing returns; (2) length normalization — penalizes documents that are longer only because they repeat terms.
            </div>

            <div style={{padding:'14px',background:'var(--deep)',border:'1px solid var(--orange)33',borderRadius:'4px',marginBottom:'14px',fontFamily:'JetBrains Mono',fontSize:'12px',color:'var(--code)'}}>
              Score(D,Q) = Σ IDF(qᵢ) × <span style={{color:'var(--orange)'}}>[f(qᵢ,D) × (k+1)] / [f(qᵢ,D) + k×(1−b+b×|D|/avgdl)]</span>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px',marginBottom:'14px'}}>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',letterSpacing:'.1em'}}>SATURATION PARAMETER k = {bm25k} (higher = slower saturation)</div>
                <input type="range" min={0.5} max={3} step={0.1} value={bm25k} onChange={e=>setBm25k(+e.target.value)} style={{width:'100%',accentColor:'var(--orange)'}}/>
              </div>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',letterSpacing:'.1em'}}>LENGTH NORM b = {bm25b} (0=no norm, 1=full norm)</div>
                <input type="range" min={0} max={1} step={0.05} value={bm25b} onChange={e=>setBm25b(+e.target.value)} style={{width:'100%',accentColor:'var(--orange)'}}/>
              </div>
            </div>

            {/* TF saturation chart */}
            <Panel title="TF SATURATION CURVE (BM25 vs Raw TF)" accent="var(--orange)">
              {(()=>{
                const data = Array.from({length:20},(_,i)=>{
                  const f=i+1;
                  return { f, rawTF:f, bm25:+((f*(bm25k+1))/(f+bm25k)).toFixed(3) };
                });
                return (
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="f" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'term frequency',position:'insideBottom',offset:-3,fill:'var(--t3)',fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Legend wrapperStyle={{fontSize:'9px'}}/>
                      <Line type="monotone" dataKey="rawTF" stroke="var(--red)"    strokeWidth={2} dot={false} name="Raw TF (linear)"/>
                      <Line type="monotone" dataKey="bm25"  stroke="var(--orange)" strokeWidth={2} dot={false} name={`BM25 TF (k=${bm25k})`}/>
                    </LineChart>
                  </ResponsiveContainer>
                );
              })()}
              <InfoBox color="var(--orange)" icon="◐" style={{marginTop:'8px'}}>
                BM25's TF contribution plateaus at k+1. Even if a term appears 100 times vs 10 times, the score difference is bounded. This prevents spam-style keyword stuffing from dominating rankings.
              </InfoBox>
            </Panel>

            <div style={{display:'flex',gap:'8px',marginTop:'12px'}}>
              <input value={bm25Query} onChange={e=>setBm25Query(e.target.value)} placeholder="BM25 search query…" style={{flex:1,background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'9px 12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none'}}/>
              <Btn onClick={runBM25} color="var(--orange)">SCORE →</Btn>
            </div>
            {bm25Scores.length > 0 && (
              <div style={{marginTop:'12px'}}>
                {bm25Scores.map((s,i)=>(
                  <div key={s.label} style={{display:'flex',alignItems:'center',gap:'12px',padding:'8px 14px',background:'var(--raised)',borderRadius:'3px',marginBottom:'5px',border:`1px solid ${i===0?'var(--orange)':'var(--b0)'}`}}>
                    <span style={{fontSize:'10px',color:'var(--t3)',fontFamily:'JetBrains Mono',width:'20px'}}>#{i+1}</span>
                    <span style={{fontSize:'12px',color:'var(--t1)',flex:1,fontWeight:i===0?600:400}}>{s.label}</span>
                    <div style={{width:'140px',height:'5px',background:'var(--deep)',borderRadius:'2px'}}>
                      <div style={{height:'100%',width:`${Math.min(100,(s.score/bm25Scores[0].score)*100)}%`,background:i===0?'var(--orange)':P[i%P.length],borderRadius:'2px'}}/>
                    </div>
                    <span style={{fontSize:'11px',color:i===0?'var(--orange)':P[i%P.length],fontFamily:'JetBrains Mono',fontWeight:700,width:'52px',textAlign:'right'}}>{s.score}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab==='concepts' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {[
              {title:'Vector Space Model (1975)',color:ACCENT,desc:'Salton\'s VSM: represent documents and queries as vectors in term space. Rank by cosine similarity. Revolutionary for its time — replaced Boolean retrieval. Still competitive baseline. Foundation of all sparse retrieval.'},
              {title:'TF-IDF vs Word Count',color:'var(--cyan)',desc:'Raw word count favors long docs and common words. TF-IDF: term freq × rarity gives discriminative score. "the" has high TF but near-zero IDF → low TF-IDF. "quantum" has lower TF but high IDF → high TF-IDF in relevant docs.'},
              {title:'The Curse of Dimensionality',color:'var(--violet)',desc:'TF-IDF vectors live in |V|-dimensional space (50K+ dims). Most pairs become equidistant — cosine fails to distinguish. Solution: dimensionality reduction (LSA/SVD to 300d) or dense neural embeddings (BERT, 768d but meaningful distances).'},
              {title:'LSA — Latent Semantic Analysis',color:'var(--gold)',desc:'Apply SVD to TF-IDF matrix: M = UΣVᵀ. Keep top-k singular values (k=200-500). Captures latent semantic structure: "car" and "automobile" cluster together. Synonymy handled. Polysemy still a problem (same word, different meanings).'},
              {title:'Inverted Index',color:'var(--green)',desc:'Production IR: don\'t compute TF-IDF over all docs for every query. Build inverted index: term → list of (doc_id, TF) pairs. BM25 scoring only touches docs containing query terms. Elasticsearch stores term vectors + BM25 precomputed.'},
              {title:'Neural Retrieval (Dense)',color:'var(--red)',desc:'DPR (Dense Passage Retrieval): BERT encodes query and doc separately → 768d dense vectors → FAISS ANN search. ColBERT: late interaction, token-level matching. 5-20x better recall than BM25 on open-domain QA. Hybrid BM25+Dense = best of both worlds.'},
            ].map(({title,color,desc})=>(
              <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
              </div>
            ))}
          </div>

          {/* TF-IDF to BERT evolution */}
          <Panel title="EVOLUTION OF TEXT REPRESENTATIONS" accent={ACCENT}>
            <div style={{display:'flex',gap:'4px',overflowX:'auto',padding:'4px 0'}}>
              {[
                {era:'1960s',name:'Boolean',desc:'AND/OR/NOT operators. Exact match only. No ranking.',color:'var(--t3)'},
                {era:'1975',name:'TF-IDF + VSM',desc:'Vector space, cosine sim. Ranked retrieval. Still widely used.',color:'var(--gold)'},
                {era:'1994',name:'BM25',desc:'Probabilistic TF saturation. Elasticsearch default today.',color:'var(--orange)'},
                {era:'2003',name:'LDA / LSA',desc:'Latent topics. Handles synonymy. 200-500d dense.',color:'var(--green)'},
                {era:'2013',name:'Word2Vec',desc:'Neural word vectors. 300d. "king-man+woman≈queen".',color:'var(--cyan)'},
                {era:'2018',name:'BERT',desc:'Contextual embeddings. 768d. Bidirectional transformer.',color:ACCENT},
                {era:'2020',name:'DPR / ColBERT',desc:'Dense retrieval. Dual encoders. ANN search.',color:'#f472b6'},
                {era:'2022+',name:'RAG + LLMs',desc:'Retrieval-augmented generation. GPT-4 + vector DB.',color:'#34d399'},
              ].map(({era,name,desc,color})=>(
                <div key={era} style={{minWidth:'120px',padding:'10px',background:'var(--raised)',border:`1px solid ${color}44`,borderRadius:'3px',textAlign:'center',flexShrink:0}}>
                  <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>{era}</div>
                  <div style={{fontSize:'11px',fontWeight:700,color,marginBottom:'5px'}}>{name}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.5}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
